import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiExternalLink } from "react-icons/fi";
import type { LogEntry } from "./../API/Models";
import { UserAvatar } from "../Components/UserAvatar";
import { useUserDirectory } from "../Components/Account/useUserDirectory";
import { useRealtimeEvent } from "../Realtime/RealtimeProvider";
import { activityToLogEntry, type ActivityPayload } from "../Realtime/activityStream";
import { badgeForLog, buildLogLink } from "../Helper/activityAction";
import { LEVEL_OPTIONS, TYPE_OPTIONS } from "../Helper/activityFilters";
import { formatExactDateFr, formatRelativeTimeFr } from "../Helper/dates";
import { auditClient, dayToIsoSince, dayToIsoUntil, type AuditFilters } from "./Audit/auditClient";
import { ChangeJournal, parseChangeJournal } from "../Components/audit/ChangeJournal";
import { AuditDiffModal } from "./Audit/AuditDiffModal";

const PAGE_SIZE = 40;

/** Types d'entrée proposés au filtre : les familles que les opérateurs cherchent réellement. */
// Axes partagés avec le profil utilisateur et la fiche « Gérer » (Helper/activityFilters).
/** Clé d'acteur STABLE d'une entrée : le courriel d'abord (les noms d'affichage se dupliquent). */
const actorKey = (entry: LogEntry): string => {
	const email = (entry.userEmail ?? "").trim();
	return email.length > 0 ? email : (entry.user ?? "");
};

interface DiffTarget {
	contentId: string;
	versionId: number;
	prevVersionId?: number;
	/** « version » = instantanés du document (saves) ; « artifact » = archives d'un déploiement. */
	kind: "version" | "artifact";
	/** Cible humaine du déploiement (« preview » / « production »), pour les libellés. */
	targetLabel?: string;
	title?: string;
}

const inputClass = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none";
const labelClass = "text-xs font-semibold uppercase tracking-wide text-slate-500";

/**
 * Page Audit : le journal des actions utilisateur avec pagination et filtres SERVEUR (acteur,
 * action, niveau, période, texte), flux temps réel, et pour chaque save le journal des
 * changements (avant/après, blocs retirés) plus le diff complet entre les deux versions.
 * /logs reste la vue technique brute ; ici on répond à « qui a changé quoi, et en quoi ».
 */
const Audit: React.FC = () => {
	const { entries: directory, resolveId, resolvePicture } = useUserDirectory();

	const [actor, setActor] = useState("");
	const [type, setType] = useState("");
	const [level, setLevel] = useState("");
	const [sinceDay, setSinceDay] = useState("");
	const [untilDay, setUntilDay] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");

	const [page, setPage] = useState(1);
	const [items, setItems] = useState<LogEntry[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [expandedKey, setExpandedKey] = useState<string | null>(null);
	const [pendingLive, setPendingLive] = useState(0);
	const [diffTarget, setDiffTarget] = useState<DiffTarget | null>(null);

	// Recherche debouncée : on n'interroge le serveur qu'au calme (400 ms), pas à chaque frappe.
	useEffect(() => {
		const handle = window.setTimeout(() => setSearch(searchInput), 400);
		return () => window.clearTimeout(handle);
	}, [searchInput]);

	const filters: AuditFilters = useMemo(
		() => ({
			actor,
			type,
			level,
			since: dayToIsoSince(sinceDay),
			until: dayToIsoUntil(untilDay),
			search
		}),
		[actor, type, level, sinceDay, untilDay, search]
	);
	const hasFilters = Boolean(actor || type || level || sinceDay || untilDay || search);

	// Tout changement de filtre ramène à la première page.
	useEffect(() => {
		setPage(1);
	}, [actor, type, level, sinceDay, untilDay, search]);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await auditClient.paged(page, PAGE_SIZE, filters);
			setItems(res.items ?? []);
			setTotal(res.total ?? 0);
			setPendingLive(0);
		} catch {
			setItems([]);
			setTotal(0);
			setError("Le journal d'audit n'a pas pu être chargé.");
		} finally {
			setLoading(false);
		}
	}, [page, filters]);

	useEffect(() => {
		load();
	}, [load]);

	// Temps réel : en page 1 sans filtre, la nouvelle entrée s'insère en tête ; sinon un bandeau
	// compte les arrivées (les injecter fausserait la page filtrée/paginée servie par le serveur).
	const liveStateRef = useRef({ page, hasFilters });
	liveStateRef.current = { page, hasFilters };
	useRealtimeEvent("activity", (payload: ActivityPayload) => {
		const entry = activityToLogEntry(payload);
		const { page: currentPage, hasFilters: filtered } = liveStateRef.current;
		if (currentPage === 1 && !filtered) {
			setItems((prev) => [entry, ...prev.filter((e) => (e.id ?? "") !== (entry.id ?? "-"))].slice(0, PAGE_SIZE));
			setTotal((prev) => prev + 1);
		} else {
			setPendingLive((prev) => prev + 1);
		}
	});

	const resetFilters = () => {
		setActor("");
		setType("");
		setLevel("");
		setSinceDay("");
		setUntilDay("");
		setSearchInput("");
		setSearch("");
	};

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const rowKey = (entry: LogEntry, index: number) => entry.id ?? `${entry.createdAt ?? ""}-${index}`;

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Audit</p>
						<h1 className="text-2xl font-semibold text-slate-900">Journal des actions</h1>
						<p className="text-xs text-slate-500">
							Qui a changé quoi : chaque sauvegarde détaille ses champs (avant / après) et ses blocs.
						</p>
					</div>
					<button
						type="button"
						onClick={load}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						{loading ? "Chargement…" : "Actualiser"}
					</button>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
					<div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-6">
						<div className="space-y-1.5">
							<label className={labelClass}>Acteur</label>
							<select className={inputClass} value={actor} onChange={(e) => setActor(e.target.value)}>
								<option value="">Toute l'équipe</option>
								{directory.map((member) => {
									const key = (member.email ?? member.name ?? "").trim();
									if (!key) return null;
									return (
										<option key={member.id} value={key}>
											{member.name ?? member.email}
										</option>
									);
								})}
							</select>
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>Action</label>
							<select className={inputClass} value={type} onChange={(e) => setType(e.target.value)}>
								{TYPE_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>Niveau</label>
							<select className={inputClass} value={level} onChange={(e) => setLevel(e.target.value)}>
								{LEVEL_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>Du</label>
							<input type="date" className={inputClass} value={sinceDay} onChange={(e) => setSinceDay(e.target.value)} />
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>Au</label>
							<input type="date" className={inputClass} value={untilDay} onChange={(e) => setUntilDay(e.target.value)} />
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>Recherche</label>
							<input
								type="text"
								placeholder="Message ou détails"
								className={inputClass}
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
							/>
						</div>
					</div>
					{hasFilters && (
						<button
							type="button"
							onClick={resetFilters}
							className="mt-4 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
						>
							Réinitialiser les filtres
						</button>
					)}
				</section>

				{pendingLive > 0 && (
					<button
						type="button"
						onClick={() => {
							setPage(1);
							load();
						}}
						className="w-full rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 transition hover:bg-sky-100"
					>
						{pendingLive} nouvelle{pendingLive > 1 ? "s" : ""} entrée{pendingLive > 1 ? "s" : ""} : afficher
					</button>
				)}

				<section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
					{loading ? (
						<ul className="divide-y divide-slate-100">
							{Array.from({ length: 8 }).map((_, i) => (
								<li key={`audit-skel-${i}`} className="flex items-center gap-3 px-5 py-3.5">
									<span className="h-7 w-7 animate-pulse rounded-full bg-slate-100" />
									<div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
								</li>
							))}
						</ul>
					) : error ? (
						<div className="px-5 py-16 text-center text-sm text-rose-600">{error}</div>
					) : items.length === 0 ? (
						<div className="px-5 py-16 text-center text-sm text-slate-500">
							Aucune entrée ne correspond à ces critères.
						</div>
					) : (
						<ul className="divide-y divide-slate-100">
							{items.map((entry, index) => {
								const key = rowKey(entry, index);
								const badge = badgeForLog(entry);
								const link = buildLogLink(entry);
								const expanded = expandedKey === key;
								const actorId = resolveId(actorKey(entry));
								const changes = entry.properties?.["changes"];
								const hasJournal = Boolean(parseChangeJournal(changes));
								const versionId = Number(entry.properties?.["versionId"] ?? "");
								const prevVersionId = Number(entry.properties?.["prevVersionId"] ?? "");
								// Les entrées de DÉPLOIEMENT portent la paire d'artefacts archivés : le même
								// bouton ouvre alors le diff « ce que ce déploiement a changé sur la cible ».
								const artifactId = Number(entry.properties?.["artifactVersionId"] ?? "");
								const prevArtifactId = Number(entry.properties?.["prevArtifactVersionId"] ?? "");
								const contentId = entry.properties?.["contentId"];
								const hasVersionDiff = Number.isFinite(versionId) && versionId > 0;
								const hasArtifactDiff = Number.isFinite(artifactId) && artifactId > 0;
								const canDiff = Boolean(contentId) && (hasVersionDiff || hasArtifactDiff);
								const deployTarget = (entry.type ?? "").includes("production") ? "production" : "preview";
								return (
									<li key={key}>
										<button
											type="button"
											onClick={() => setExpandedKey(expanded ? null : key)}
											className="flex w-full items-center gap-3 px-5 py-2.5 text-left transition hover:bg-slate-50"
										>
											<span className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${badge.chip}`}>
												{badge.label}
											</span>
											<span className="min-w-0 flex-1 truncate text-sm text-slate-800">{entry.message}</span>
											{hasJournal && (
												<span className="hidden shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 sm:inline">
													détail
												</span>
											)}
											<span className="flex shrink-0 items-center gap-1.5">
												<UserAvatar name={entry.user} id={actorId ?? undefined} pictureUrl={resolvePicture(actorKey(entry)) ?? undefined} sizeClass="h-6 w-6" />
												<span className="hidden max-w-[10rem] truncate text-xs text-slate-500 md:inline">{entry.user}</span>
											</span>
											<span className="shrink-0 text-[11px] text-slate-400" title={formatExactDateFr(entry.createdAt)}>
												{formatRelativeTimeFr(entry.createdAt)}
											</span>
										</button>
										{expanded && (
											<div className="space-y-3 border-t border-slate-100 bg-slate-50/60 px-5 py-4">
												{entry.details && <p className="text-xs text-slate-600">{entry.details}</p>}
												{hasJournal && (
													<div className="rounded-xl border border-slate-200 bg-white p-3">
														<p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
															Changements
														</p>
														<ChangeJournal changesJson={changes} />
													</div>
												)}
												<div className="flex flex-wrap items-center gap-2">
													{canDiff && (
														<button
															type="button"
															onClick={() =>
																setDiffTarget(
																	hasVersionDiff
																		? {
																				kind: "version",
																				contentId: contentId!,
																				versionId,
																				prevVersionId: Number.isFinite(prevVersionId) && prevVersionId > 0 ? prevVersionId : undefined,
																				title: entry.message ?? undefined
																		  }
																		: {
																				kind: "artifact",
																				contentId: contentId!,
																				versionId: artifactId,
																				prevVersionId: Number.isFinite(prevArtifactId) && prevArtifactId > 0 ? prevArtifactId : undefined,
																				targetLabel: deployTarget,
																				title: entry.message ?? undefined
																		  }
																)
															}
															className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800"
														>
															{hasVersionDiff ? "Voir le diff complet" : "Voir les changements du déploiement"}
														</button>
													)}
													{link && (
														<Link
															to={link}
															className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
														>
															<FiExternalLink className="h-3.5 w-3.5" /> Ouvrir le contenu
														</Link>
													)}
													<span className="ml-auto text-[11px] text-slate-400">{formatExactDateFr(entry.createdAt)}</span>
												</div>
											</div>
										)}
									</li>
								);
							})}
						</ul>
					)}
					<div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
						<span className="text-xs text-slate-500">
							{total > 0 ? `${total} entrée${total > 1 ? "s" : ""} · page ${page} / ${totalPages}` : "Aucune entrée"}
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={loading || page <= 1}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
							>
								<FiChevronLeft className="h-3.5 w-3.5" /> Précédent
							</button>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={loading || page >= totalPages}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
							>
								Suivant <FiChevronRight className="h-3.5 w-3.5" />
							</button>
						</div>
					</div>
				</section>
			</div>

			{diffTarget && (
				<AuditDiffModal
					open
					onClose={() => setDiffTarget(null)}
					contentId={diffTarget.contentId}
					versionId={diffTarget.versionId}
					prevVersionId={diffTarget.prevVersionId}
					kind={diffTarget.kind}
					targetLabel={diffTarget.targetLabel}
					title={diffTarget.title}
				/>
			)}
		</div>
	);
};

export default Audit;
